/**
 * Unified client initialization for workers and frontends
 * Consolidates scattered initialization patterns to prevent configuration bugs
 */

import { newWorkersChannelClient, useSandboxWrappers } from 'workers-channel-js'
import { newNamespaceClient as createNamespaceClient } from 'workers-namespace-js'
import { wrappers } from './index.js'
import { defaultSandbox } from './defaults.js'

// Service URL constants
export const CHANNEL_SERVICE_URL = 'https://channel.monoqueue.com'
export const CHANNEL_WEBSOCKET_URL = 'wss://channel.monoqueue.com/ws'
export const NAMESPACE_SERVICE_URL = 'https://namespace.monoqueue.com'

/**
 * Create a worker client with standard configuration
 *
 * @param {Object} options
 * @param {string} options.privateKey - Worker's Ed25519 private key (64-char hex)
 * @param {Object} options.sandbox - RPC method implementations
 * @param {Array|Function} [options.middlewares] - Custom middleware wrappers array, or factory fn({ namespaceClient }) => array
 * @param {Array} [options.permissions] - Permission rules for auto-generated middleware (requestLog + SimplePermissionControl)
 * @param {number} [options.callTimeout=30000] - RPC call timeout in milliseconds
 * @param {boolean} [options.enableNamespace=true] - Enable namespace client for callByName
 * @param {string} [options.callTransport='ws'] - Transport mode: 'ws', 'http', or 'auto'
 * @param {string} [options.serviceUrl] - Override channel service URL
 * @param {string} [options.websocketUrl] - Override WebSocket URL
 * @param {string} [options.namespaceUrl] - Override namespace service URL
 * @returns {Object} Workers-channel client instance
 */
export const createWorkerClient = ({
  privateKey,
  sandbox,
  middlewares,
  permissions,
  callTimeout = 30000,
  enableNamespace = true,
  callTransport = 'ws',
  serviceUrl = CHANNEL_SERVICE_URL,
  websocketUrl = CHANNEL_WEBSOCKET_URL,
  namespaceUrl = NAMESPACE_SERVICE_URL,
}) => {
  if (!privateKey) throw new Error('[createWorkerClient] privateKey is required')
  if (!sandbox) throw new Error('[createWorkerClient] sandbox is required')

  // Create namespace client if enabled
  let namespaceClient = null
  if (enableNamespace) {
    namespaceClient = createNamespaceClient({
      namespaceUrl,
      selector: 'random',
    })
  }

  // Merge default methods (worker's own methods take priority)
  let finalSandbox = { ...defaultSandbox, ...sandbox }

  // Apply middleware: custom array/function or auto-generated from permissions
  if (middlewares) {
    const resolved = typeof middlewares === 'function' ? middlewares({ namespaceClient }) : middlewares
    finalSandbox = useSandboxWrappers(finalSandbox, resolved)
  } else if (permissions) {
    finalSandbox = useSandboxWrappers(finalSandbox, [
      wrappers.requestLog,
      wrappers.SimplePermissionControl(permissions, { getNamespaceClient: () => namespaceClient }),
    ])
  }

  const client = newWorkersChannelClient({
    serviceUrl,
    websocketUrl,
    privateKey,
    sandbox: finalSandbox,
    callTransport,
    callTimeout,
    namespaceClient,
  })

  return client
}

/**
 * Create a frontend client with standard configuration
 *
 * @param {Object} options
 * @param {Function} options.getPrivateKey - Function that returns hex-encoded private key
 * @param {Function} options.getAccessToken - Async function that returns JWT token (or null)
 * @param {Object} [options.sandbox] - Optional local sandbox methods (for local-first features like session storage)
 * @param {string} [options.serviceUrl] - Override channel service URL
 * @param {string} [options.namespaceUrl] - Override namespace service URL
 * @param {number} [options.callTimeout] - Override default call timeout
 * @returns {Object} Workers-channel client instance with JWT auto-injection
 */
export const createFrontendClient = ({
  getPrivateKey,
  getAccessToken,
  sandbox,
  serviceUrl = CHANNEL_SERVICE_URL,
  namespaceUrl = NAMESPACE_SERVICE_URL,
  callTimeout,
}) => {
  if (!getPrivateKey) throw new Error('[createFrontendClient] getPrivateKey is required')
  if (!getAccessToken) throw new Error('[createFrontendClient] getAccessToken is required')

  const privateKey = getPrivateKey()
  if (!privateKey) throw new Error('[createFrontendClient] getPrivateKey() returned null/undefined')

  const namespaceClient = createNamespaceClient({
    namespaceUrl,
    selector: 'random',
  })

  const clientConfig = {
    serviceUrl,
    privateKey,
    callTransport: 'http',
    namespaceClient,
    ...(sandbox && { sandbox }),
  }

  if (callTimeout) {
    clientConfig.callTimeout = callTimeout
  }

  const client = newWorkersChannelClient(clientConfig)

  client.callWithAuth = async (worker, method, params, options = {}) => {
    const accessToken = await getAccessToken()
    if (accessToken) {
      options.headers = {
        ...options.headers,
        authorization: `Bearer ${accessToken}`,
      }
    }
    return client.call(worker, method, params, options)
  }

  if (client.callByName) {
    client.callByNameWithAuth = async (workerName, method, params, options = {}) => {
      const accessToken = await getAccessToken()
      if (accessToken) {
        options.headers = {
          ...options.headers,
          authorization: `Bearer ${accessToken}`,
        }
      }
      return client.callByName(workerName, method, params, options)
    }
  }

  return client
}

/**
 * Generate ephemeral Ed25519 private key
 * @returns {string} Hex-encoded private key (64 chars)
 */
export const generateEphemeralPrivateKey = () => {
  const crypto = globalThis.crypto || (typeof window !== 'undefined' && window.crypto)
  if (!crypto?.getRandomValues) {
    throw new Error('[generateEphemeralPrivateKey] Web Crypto API not available')
  }

  const bytes = crypto.getRandomValues(new Uint8Array(32))
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}
